// TypeScript interfaces para a Central de Comunicação

// Tipos de departamento
export type Department = 'recepcao' | 'financeiro' | 'turismo' | 'copa' | 'restaurante' | 'outro'

// Tipos de autor das mensagens
export type MessageAuthor = 'guest' | 'agent' | 'bot' | 'ai-suggestion' | 'ai-auto'

// Status de entrega das mensagens
export type DeliveryStatus = 'sent' | 'delivered' | 'read' | 'failed'

// Sentimentos analisados pela IA
export type Sentiment = 'positive' | 'neutral' | 'negative'

// Idiomas suportados
export type Language = 'pt' | 'en' | 'es' | 'fr' | 'de' | 'it'

// Modos de operação do concierge
export type ConciergeMode = 'manual' | 'assisted' | 'autopilot'

// Interface principal da sessão
export interface Session {
  id: number
  sessionKey: string
  hotelId: number
  guestName: string
  room: string
  reservationId: string
  cpfMasked: string
  department: Department
  lang: Language
  connected: boolean
  lastMessageAt: Date
  unreadCount?: number
  lastMessage?: Message
  conciergeMode?: ConciergeMode
}

// Interface para sessões agrupadas (resolve problema de duplicatas)
export interface GroupedSession {
  id: number // ID da primeira sessão original (para backend)
  sessionKey: string
  hotelId: number
  guestName: string
  room: string
  reservationId: string
  cpfMasked: string
  department: Department
  lang: Language
  connected: boolean
  lastMessageAt: Date
  totalUnreadCount: number
  lastMessage?: Message
  sessionCount: number // Para debug de duplicatas
  conciergeMode?: ConciergeMode
}

// Interface da mensagem
export interface Message {
  id: number
  sessionId: number
  author: MessageAuthor
  text: string
  imageUrl?: string
  lang: Language
  sentiment?: Sentiment
  channel: Department
  deliveryStatus: DeliveryStatus
  sentAt?: Date
  deliveredAt?: Date
  readAt?: Date
  created: Date
  // 🤖 Metadados para mensagens geradas por IA
  metadata?: {
    isAISuggestion?: boolean
    isAIAutomatic?: boolean
    aiConfidence?: number
    originalPrompt?: string
    processingTime?: number
  }
}

// Interface para resposta da análise da IA (serviço CAS)
export interface AIAnalysisResponse {
  success: boolean
  suggestion: string
  sentiment: Sentiment
  confidence: number
  metadata?: {
    processingTime?: number
    model?: string
    sqlQuery?: string
    rawData?: any
  }
}

// Interface para sugestões de resposta da IA (uso interno)
export interface AIResponseSuggestion {
  response: string
  confidence: number
  suggestedDepartment?: Department
  category?: 'pergunta' | 'sugestao' | 'reclamacao'
}

// Interface para filtros das sessões
export interface SessionFilters {
  showUnreadOnly: boolean
  showConnectedOnly: boolean
  departments: Department[]
  searchQuery: string
}

// Interface para estatísticas
export interface CommunicationStats {
  totalSessions: number
  activeSessions: number
  unreadMessages: number
  averageResponseTime: number
  messagesByDepartment: Record<Department, number>
}

// Interface para dados do WebSocket
export interface SocketMessage {
  sessionKey: string
  hotelId: number
  author: MessageAuthor
  text: string
  lang: Language
  channel: Department
  imageUrl?: string
}

// Interface para eventos do WebSocket
export interface SocketEvents {
  // Eventos enviados pelo agente
  'agent-connect': { hotelId: number }
  'agent-join-session': { sessionKey: string; hotelId: number }
  'send-message': SocketMessage
  'translate-message': { text: string; fromLang: Language; toLang: Language }
  'voice-command': { command: string; lang: Language; sessionKey: string }

  // Eventos recebidos pelo agente
  'agent-connected': { agentId: string; hotelId: number }
  'active-sessions': { sessions: Session[] }
  'guest-message': { sessionKey: string; message: Message; unreadCount: number; guestName: string }
  'new-message': Message
  'session-connected': Session
  'session-disconnected': Session
  'agent-session-joined': { sessionKey: string; messages: Message[] }
  'message-translated': { originalText: string; translatedText: string; fromLang: Language; toLang: Language }
}

// Interface para análise de sentimento
export interface SentimentAnalysis {
  text: string
  sentiment: Sentiment
  confidence: number
  keywords: string[]
}

// Interface para tradução
export interface TranslationRequest {
  text: string
  fromLang: Language
  toLang: Language
}

export interface TranslationResponse {
  originalText: string
  translatedText: string
  fromLang: Language
  toLang: Language
  confidence: number
}

// Interface para busca
export interface SearchParams {
  hotelId: number
  query: string
  department?: Department
  dateRange?: {
    start: Date
    end: Date
  }
}

export interface SearchResult {
  sessionKey: string
  guestName: string
  room: string
  message: Message
  department: Department
}

// Interface para respostas rápidas
export interface QuickReply {
  id: string
  text: string
  department?: Department
  category: string
  order: number
}

// Interface para configurações do chat
export interface ChatSettings {
  autoTranslate: boolean
  defaultLanguage: Language
  soundNotifications: boolean
  showTypingIndicator: boolean
  markAsReadOnView: boolean
  aiSuggestions: boolean
}

// Cores por departamento
export const DEPARTMENT_COLORS: Record<Department, string> = {
  recepcao: '#3699FF',
  financeiro: '#50CD89',
  turismo: '#F1BC00',
  copa: '#F1416C',
  restaurante: '#FFA800',
  outro: '#7E8299'
}

// Labels dos departamentos
export const DEPARTMENT_LABELS: Record<Department, string> = {
  recepcao: 'Recepção',
  financeiro: 'Financeiro',
  turismo: 'Turismo',
  copa: 'Copa',
  restaurante: 'Restaurante',
  outro: 'Outro'
}

// Labels dos idiomas
export const LANGUAGE_LABELS: Record<Language, string> = {
  pt: 'Português',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano'
}

// Respostas rápidas padrão
export const DEFAULT_QUICK_REPLIES: QuickReply[] = [
  {
    id: '1',
    text: 'Obrigado pelo contato! Como posso ajudá-lo?',
    category: 'greeting',
    order: 1
  },
  {
    id: '2',
    text: 'Vou verificar essa informação para você.',
    category: 'info',
    order: 2
  },
  {
    id: '3',
    text: 'Peço desculpas pelo inconveniente.',
    category: 'apology',
    order: 3
  },
  {
    id: '4',
    text: 'Enviarei alguém ao seu quarto imediatamente.',
    category: 'service',
    order: 4
  },
  {
    id: '5',
    text: 'Sua solicitação foi registrada.',
    category: 'confirmation',
    order: 5
  }
]
// Labels dos modos de operação do concierge
export const CONCIERGE_MODE_LABELS: Record<ConciergeMode, string> = {
  manual: 'Manual',
  assisted: 'Assistido',
  autopilot: 'Piloto Automático'
}

// Descrições dos modos de operação do concierge
export const CONCIERGE_MODE_DESCRIPTIONS: Record<ConciergeMode, string> = {
  manual: 'Funcionário responde tudo manualmente',
  assisted: 'IA sugere respostas para aprovação do funcionário',
  autopilot: 'IA responde automaticamente ao hóspede'
}
